import * as React from 'react';
import {
    useParams,
    useNavigate,
    Navigate
} from 'react-router-dom';
import * as classNames from 'classnames';
import SessionMetadataTable from '../Shared/SessionMetadataTable';
import SpirometryResultsTable from './SpirometryResultsTable';
import { SessionMetadata, FvcFlow, OverreadStatusEnum, FixedCommentMutation, TestAcceptabilityMutation, OverreadChallengeStatusEnum, OverreadStatusIdEnum, FvcTestFlow, FvcDataPoint, SpirometryAnalysisSet, HomeSpirometryStatus } from "../../misc/api/apiTypes";
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { ToggleSessionFixedCommentsVisibilityAction } from '../../PulmonaryFunctionTests/Shared/UseFixedComments';
import { SpirometryResults, StudyRelevantParameters } from '../../PulmonaryFunctionTests/Shared/PFTAPITypes';
import { SpirometryAnalysisSetTypeEnum } from '../../misc/api/ApiSpirometryAnalysisSetType';
import { DoneLoadingReversibilityChangesAction, LoadingBestValuesAction, LoadingReversibilityChangesAction, UpdateRepeatabilityAction } from '../Shared/UseAcceptability';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { FixedCommentSetTypeEnum } from '../../misc/api/FixedComments/FixedCommentSetTypes';
import { FixedCommentEntityTypeEnum } from '../../misc/api/FixedComments/FixedCommentSetEntityTypes';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { SessionNote, FvcAnalysisSetTest, FvcAnalysisSet, FvcAnalysisSetMutation } from 'webportalclient';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { ChallengeAlert } from '../Shared/ChallengeAlert';
import { extractFirstCycle, extractExpiratoryCurveData, adjustTimeToTZero } from "./FvcSessionAnalyzer";
import { useSecurityContext } from '../../Core/SecurityContext';
import { SpirometryGraphs, FvcGraphDataMapping } from './SpirometryGraphs';
import { SummaryPanel } from '../Shared/SummaryPanel';
import { useGraphs } from '../Shared/UseGraphs';
import { FvcSession } from 'webportalclient';
import { useAcceptabilityNextGen, AcceptabilityNextGenState, UpdateReversibilityChangeNextGenAction, UpdateGradingAction } from '../Shared/UseAcceptabilityNextGen';
import { calculateBestValuesGrading } from './SpirometryBestValuesGrading';
import { LoadAnalysisSetSelectionsAction, useNextGenFixedComments } from './Testing/UseNextGenFixedComments';
import { OverreadingAcceptabilityDashboard } from '../Shared/OverreadingAcceptabilityDashboard';
import { useWebPortalApiClient, useWebPortalLegacyClient, WebPortalLegacyClientInternal } from '../../misc/api/WebPortalTokenClient';
import { GoBackToList } from '../Shared/AnalysisHelpers';
import { ParameterTypeName } from '../../misc/api/ParameterTypes';
import { ModalSize } from '../../misc/components/modal';

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: "view" | "overread";
}

export interface GraphTestData extends FvcTestFlow {
    ExpiratoryOnlyData: FvcDataPoint[];
    TZeroAdjustedExpiratoryOnlyData: FvcDataPoint[];
    TZeroAdjustedData: FvcDataPoint[];
    Valid: boolean;
}

export const calculateReversibilityChange = async (legacyClient: WebPortalLegacyClientInternal, acceptabilityState: AcceptabilityNextGenState, acceptabilityDispatch: React.Dispatch<any>, fvcSession: FvcSession) => {
    acceptabilityDispatch(LoadingReversibilityChangesAction.create());

    if ((acceptabilityState.analysisSet.type === SpirometryAnalysisSetTypeEnum.Site && fvcSession.status === HomeSpirometryStatus.Completed)) {
        acceptabilityDispatch(DoneLoadingReversibilityChangesAction.create());
        return;
    }

    const reversibilityChangeParameters = await legacyClient.calculateReversibilityChange(acceptabilityState.analysisSet, fvcSession);
    const fev1 = reversibilityChangeParameters.find(bt => bt.ParameterType === ParameterTypeName.PARAM_FEV1);
    acceptabilityDispatch(UpdateReversibilityChangeNextGenAction.create(
        {
            absolute: fev1.Value,
            percentage: fev1.PercentageOfChange
        }
    ));
    acceptabilityDispatch(DoneLoadingReversibilityChangesAction.create());
}

export const SpirometryAnalysisScreen = (): JSX.Element => {
    const { analysisSetId, viewState } = useParams();
    const navigate = useNavigate();
    const { study, fvcSettings } = useCurrentStudyConfigurationContext();
    const { currentUser, currentUserPermissions } = useSecurityContext();

    const [sessionMetadata, setSessionMetadata] = React.useState<SessionMetadata>(null);
    const [analysisSet, setAnalysisSet] = React.useState<SpirometryResults>(null);
    const [flowData, setFlowData] = React.useState<GraphTestData[]>([]);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>([]);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | undefined>();
    const client = useWebPortalApiClient();
    const [technicianTestCommentsAvailable, setTechnicianTestCommentsAvailable] = React.useState(false);
    const [technicianSessionCommentsAvailable, setTechnicianSessionCommentsAvailable] = React.useState(false);
    const [isShowingExpiratoryOnly, setIsShowingExpiratoryOnly] = React.useState(false);
    const [adjustedToTZero, setAdjustedToTZero] = React.useState(false);
    const [subjectAge, setSubjectAge] = React.useState<number>();
    const [studyFvcRelevantParameters, setStudyFvcRelevantParameters] = React.useState<StudyRelevantParameters>(null);
    const [fvcSession, setFvcSession] = React.useState<FvcSession>();
    const [overreadAnalysisSet, setOverreadAnalysisSet] = React.useState<FvcAnalysisSet>();
    const [siteAnalysisSet, setSiteAnalysisSet] = React.useState<FvcAnalysisSet>();
    const [preAnalysisSet, setPreAnalysisSet] = React.useState<FvcAnalysisSet>();
    const [noteError, setNoteError] = React.useState<string | undefined>();
    const isOverread = viewState === "overread";
    const [acceptabilityState, acceptabilityDispatch] = useAcceptabilityNextGen(overreadAnalysisSet, siteAnalysisSet);
    const legacyClient = useWebPortalLegacyClient();

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    React.useEffect(() => {
        const fetchAnalysisSet = async () => {
            const access = await client.client.getSpirometryAnalysisSet(analysisSetId, "canEdit");

            setIsViewOnly(!access.canEdit);
            const overreadPermission = currentUserPermissions.find(p => p.Id == PermissionEnum.OverreadFvcSession);
            setIsViewOnly(!overreadPermission);

            if (access.canEdit && isOverread && overreadPermission) {
                setIsViewOnly(false);
                await legacyClient.startSpirometryOverread(analysisSetId);
            } else {
                setIsViewOnly(true);
            }

            const parameters = await legacyClient.getStudyFvcRelevantParameters();
            setStudyFvcRelevantParameters(parameters);

            const results = await legacyClient.getSpirometryResultData(analysisSetId);
            setAnalysisSet(results);

            if (fvcSettings?.GradingEnabled) {
                const analysisSetResult = await client.client.getSpirometryAnalysisSet(analysisSetId, "fvcGrade,fev1Grade");
                acceptabilityDispatch(UpdateGradingAction.create(analysisSetResult.fev1Grade, analysisSetResult.fvcGrade));

            }

            const sessionMetadataResponse = await legacyClient.getSessionMetadata(results.SessionId);
            setSessionMetadata(sessionMetadataResponse);

            const fvcSessionResult = await client.client.getFvcSession(sessionMetadataResponse.Id, undefined, "tests");
            setFvcSession(fvcSessionResult);
            const graphDataMapped = FvcGraphDataMapping(fvcSessionResult);
            setFlowData(graphDataMapped);

            const currentAnalysisSet = sessionMetadataResponse.AnalysisSets.find(a => a.Id === analysisSetId);
            const isQc = currentAnalysisSet.Type.Id === SpirometryAnalysisSetTypeEnum.QC;

            const overreadAnalysisSetId = isOverread || isQc ? analysisSetId : sessionMetadataResponse.AnalysisSets.find(a => a.Type.Id === SpirometryAnalysisSetTypeEnum.Overread).Id;
            const overreadAnalysisSetResult = await client.client.getSpirometryAnalysisSet(overreadAnalysisSetId, undefined, "tests($expand=test)");
            setOverreadAnalysisSet(overreadAnalysisSetResult);

            const siteAnalysisSetId = sessionMetadataResponse.AnalysisSets.find(a => a.Type.Id === SpirometryAnalysisSetTypeEnum.Site).Id;
            const siteAnalysisSetResult = await client.client.getSpirometryAnalysisSet(siteAnalysisSetId, undefined, "tests($expand=test)");
            setSiteAnalysisSet(siteAnalysisSetResult);

            if (fvcSessionResult.preSessionId !== null && fvcSessionResult.preSessionId !== undefined) {
                const preAnalysisSetResult = await client.client.getSpirometryAnalysisSets(undefined, `sessionId eq ${fvcSessionResult.preSessionId} and type eq '${overreadAnalysisSetResult.type}'`);
                setPreAnalysisSet(preAnalysisSetResult.value.length > 0 ? preAnalysisSetResult.value[0] : undefined);
            }

            fixedCommentDispatchOverreading(LoadAnalysisSetSelectionsAction.create(overreadAnalysisSetResult.fixedComments, overreadAnalysisSetResult.tests));
            fixedCommentDispatchTechnician(LoadAnalysisSetSelectionsAction.create(siteAnalysisSetResult.fixedComments, siteAnalysisSetResult.tests));

            const ageResponse = await client.client.getSubject(sessionMetadataResponse.SubjectId, "age");
            setSubjectAge(ageResponse.age);

            await fetchSessionNotes(results.SessionId);

            const studySets = await client.client.getStudyFixedCommentSets(undefined, `studyId eq ${study.id}`, undefined, "fixedCommentSets");

            setTechnicianSessionCommentsAvailable(studySets.value.find(at => at.commentSetType === FixedCommentSetTypeEnum.Technician && at.sessionType === SessionTypes.Fvc)?.fixedCommentSets.find(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Session) !== null);
            setTechnicianTestCommentsAvailable(studySets.value.find(at => at.commentSetType === FixedCommentSetTypeEnum.Technician && at.sessionType === SessionTypes.Fvc)?.fixedCommentSets.find(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Test) !== null);
        }

        fetchAnalysisSet();
    }, [analysisSetId, viewState, fvcSettings?.GradingEnabled]);

    const [graphState, graphDispatch] = useGraphs(flowData);
    const [fixedCommentStateTechnician, fixedCommentDispatchTechnician] = useNextGenFixedComments(SessionTypes.Fvc, FixedCommentSetTypeEnum.Technician, siteAnalysisSet);
    const [fixedCommentStateOverreading, fixedCommentDispatchOverreading] = useNextGenFixedComments(SessionTypes.Fvc, FixedCommentSetTypeEnum.Overread, overreadAnalysisSet);

    React.useEffect(() => {
        const fetchBestValues = async () => {
            acceptabilityDispatch(LoadingBestValuesAction.create());
            await calculateBestValuesGrading(legacyClient, acceptabilityState, acceptabilityDispatch, fvcSession, fvcSettings, acceptabilityState.changedAcceptability);

            const repeatabilityValues = await legacyClient.calculateFvcRepeatabilityForAnalysisSet(acceptabilityState.analysisSet, fvcSession);
            acceptabilityDispatch(UpdateRepeatabilityAction.create(repeatabilityValues));
        }

        const fetchReversibilityParameters = async () => {
            acceptabilityDispatch(LoadingBestValuesAction.create());
            await calculateReversibilityChange(legacyClient, acceptabilityState, acceptabilityDispatch, fvcSession);
        }

        if (acceptabilityState.analysisSet !== undefined && fvcSession !== undefined) {
            fetchBestValues();
            fetchReversibilityParameters();
        }
    }, [acceptabilityState.changedAcceptability, acceptabilityDispatch, subjectAge, fvcSettings?.GradingEnabled]);

    const graphData: GraphTestData[] = React.useMemo(() => {
        if (!(graphState.graphData && sessionMetadata && analysisSet && fixedCommentStateOverreading.areAvailableFixedCommentsLoaded !== null))
            return;

        return graphState.graphData.map(vt => {
            try {
                const sampleRate1 = Math.abs(vt.VolumeData[0].Time - vt.VolumeData[1].Time).toFixed(2);
                const sampleRate2 = Math.abs(vt.VolumeData[5].Time - vt.VolumeData[6].Time).toFixed(2);

                // TODO: The curve extraction code cannot work with an inconsistent sample rate, cut this logic short for now
                if (sampleRate1 !== sampleRate2) {
                    return {
                        TestId: vt.TestId,
                        TestNumber: vt.TestNumber,
                        ColorHex: vt.ColorHex,
                        IsDisplayed: vt.IsDisplayed,
                        VolumeData: vt.VolumeData,
                        ExpiratoryOnlyData: vt.VolumeData,
                        TZeroAdjustedExpiratoryOnlyData: vt.VolumeData,
                        TZeroAdjustedData: vt.VolumeData,
                        Valid: vt.Valid
                    }
                }
                const firstCycle = extractFirstCycle(vt.VolumeData);
                const expiratoryCycle = extractExpiratoryCurveData(vt.VolumeData, firstCycle);

                const expiratoryOnlyData = vt.VolumeData.slice(expiratoryCycle.startOfTestIndex, expiratoryCycle.endOfTestIndex);
                const tZeroAdjustedDataFull = adjustTimeToTZero(vt.VolumeData, expiratoryCycle);
                const tZeroAdjustedDataExpiratoryOnlyData = adjustTimeToTZero(expiratoryOnlyData, expiratoryCycle);

                return {
                    TestId: vt.TestId,
                    TestNumber: vt.TestNumber,
                    ColorHex: vt.ColorHex,
                    IsDisplayed: vt.IsDisplayed,
                    VolumeData: vt.VolumeData,
                    ExpiratoryOnlyData: expiratoryOnlyData,
                    TZeroAdjustedExpiratoryOnlyData: tZeroAdjustedDataExpiratoryOnlyData,
                    TZeroAdjustedData: tZeroAdjustedDataFull,
                    Valid: vt.Valid
                }
            } catch (ex) {
                return {
                    TestId: vt.TestId,
                    TestNumber: vt.TestNumber,
                    ColorHex: vt.ColorHex,
                    IsDisplayed: vt.IsDisplayed,
                    VolumeData: vt.VolumeData,
                    ExpiratoryOnlyData: [],
                    TZeroAdjustedExpiratoryOnlyData: [],
                    TZeroAdjustedData: [],
                    Valid: vt.Valid
                };
            }
        })
    }, [graphState.graphData, sessionMetadata, analysisSet, fixedCommentStateOverreading.areAvailableFixedCommentsLoaded]);

    if (!(flowData.length > 0 && sessionMetadata && analysisSet && acceptabilityState.analysisSet && siteAnalysisSet && fixedCommentStateOverreading.areAvailableFixedCommentsLoaded !== null)) {
        return (<LoadingWithMessage message="Loading Spirometry Data..." />);
    }

    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id === PermissionEnum.AddFvcSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id === PermissionEnum.ViewFvcSessionNotes);

    const pageAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === analysisSetId);
    const pageAnalysisSetType = pageAnalysisSet.Type.Id;
    const isQcAnalysisSet = pageAnalysisSetType === SpirometryAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = pageAnalysisSetType === SpirometryAnalysisSetTypeEnum.Site;
    const isOverreadAnalysisSet = pageAnalysisSetType === SpirometryAnalysisSetTypeEnum.Overread;
    const isUnauthorized = isQcAnalysisSet && pageAnalysisSet.Analyst.Username && pageAnalysisSet.Analyst.Username != currentUser.Username;

    if (isUnauthorized) {
        return <Navigate to={{ pathname: "/unauthorized" }} />;
    }

    if (isOverread && isSiteAnalysisSet) {
        return <Navigate to={{ pathname: `/Spirometry/Analysis/${analysisSetId}/view` }} />;
    }

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);

        const sessionFixedCommentsMutation = fixedCommentStateOverreading.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentStateOverreading.testFixedComments.map(x => ({
            TestId: x.testId, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.analysisSet.tests.map(x => ({
            TestId: x.testId,
            Accepted: x.accepted
        } as TestAcceptabilityMutation));

        try {

            const mutation = acceptabilityState.analysisSet as FvcAnalysisSetMutation;
            const response1 = await client.client.patchSpirometryAnalysisSet(mutation, analysisSetId);

            for (const test of acceptabilityState.analysisSet.tests) {
                await client.client.patchSpirometryAnalysisSetTest({
                    ...test,
                    fixedComments: undefined,
                    test: undefined,
                } as FvcAnalysisSetTest, test.id, "");
            }

            const response2 = await legacyClient.updateAcceptabilityFvc(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, acceptabilityState.analysisSet, fvcSession, annotation);

            if (response2.AnalysisSetId !== analysisSetId || !response1) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            GoBackToList(navigate, currentUserPermissions, "/Spirometry", PermissionEnum.OverreadFvcSession, "/Spirometry/Analysis");
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }

    }

    const addNewNote = async (annotation: string) => {
        try {
            await legacyClient.putSessionNotes(analysisSet.SessionId, annotation);
        } catch (ex) {
            setNoteError(ex.Message);
            return;
        }
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const overreadingDisabled = viewState === "overread"
        && (fixedCommentStateOverreading.areAvailableFixedCommentsLoaded === false || (fixedCommentStateOverreading.areFixedCommentsLoaded === undefined || fixedCommentStateOverreading.areFixedCommentsLoaded === null || fixedCommentStateOverreading.areFixedCommentsLoaded === false));

    const tidalToggleClasses = classNames(
        'fas',
        'fa-fw',
        {
            'fa-toggle-on': isShowingExpiratoryOnly,
            'fa-toggle-off': !isShowingExpiratoryOnly
        }
    );

    const tZeroToggleClasses = classNames(
        'fas',
        'fa-fw',
        {
            'fa-toggle-on': adjustedToTZero,
            'fa-toggle-off': !adjustedToTZero
        }
    );

    return (
        <React.StrictMode>
            <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
                Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>

            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            {!isQcAnalysisSet && !isSiteAnalysisSet && !isViewOnly && sessionMetadata.ChallengeStatusId === OverreadChallengeStatusEnum.Challenged && !sessionMetadata.ChallengeIsClosed ?
                <ChallengeAlert
                    sessionId={sessionMetadata.Id}
                    sessionType={SessionTypes.Fvc}
                    onRejectComplete={() => GoBackToList(navigate, currentUserPermissions, "/Spirometry", PermissionEnum.OverreadFvcSession, "/Spirometry/Analysis")}
                /> : null}

            <h2 className="text-primary">Spirometry Data</h2>
            {!isQcAnalysisSet ?
                <SessionMetadataTable
                    sessionMetadata={sessionMetadata}
                    selectedAnalysisSetId={analysisSetId}
                />
                : <Alert show={true} category={AlertCategoryEnum.Info}>Session Information is hidden from view in this workflow</Alert>}
            <h2 className="text-primary">Charts</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="btn-group pull-right">
                                <button className="btn btn-primary" onClick={() => setIsShowingExpiratoryOnly(!isShowingExpiratoryOnly)}>
                                    <i className={tidalToggleClasses} aria-hidden="true"></i>&nbsp;Expiratory Only
                                </button>
                                <button className="btn btn-primary" onClick={() => setAdjustedToTZero(!adjustedToTZero)}>
                                    <i className={tZeroToggleClasses} aria-hidden="true"></i>&nbsp;TZero Adjust
                                </button>
                            </div>
                        </div>
                    </div>
                    <SpirometryGraphs data={graphData} tZeroAdjust={adjustedToTZero} expiratoryOnlyVisible={isShowingExpiratoryOnly} />
                </div>
            </div>

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <SpirometryResultsTable
                        isViewOnly={isViewOnly || overreadingDisabled || acceptabilityState.loadingBestValues}
                        session={fvcSession}
                        analysisSet={acceptabilityState.analysisSet}
                        preAnalysisSet={preAnalysisSet}
                        isSiteAnalysisSet={isSiteAnalysisSet}
                        graphState={graphState}
                        graphDispatch={graphDispatch}
                        fixedCommentsStateTechnician={fixedCommentStateTechnician}
                        fixedCommentsDispatchTechnician={fixedCommentDispatchTechnician}
                        fixedCommentsStateOverreading={fixedCommentStateOverreading}
                        fixedCommentsDispatchOverreading={fixedCommentDispatchOverreading}
                        acceptabilityState={acceptabilityState}
                        acceptabilityDispatch={acceptabilityDispatch}
                        siteAnalysisSet={siteAnalysisSet}
                        technicianSessionCommentsAvailable={technicianSessionCommentsAvailable}
                        technicianTestCommentsAvailable={technicianTestCommentsAvailable}
                    />
                </div>
            </div>
            {
                hasViewNotePermission ?
                    <SummaryPanel
                        notesButtonDisabled={acceptabilityState.analysisSet.type === SpirometryAnalysisSetTypeEnum.QC}
                        notesButtonName="Session Notes"
                        showNotesCount={true}
                        isLoading={acceptabilityState.loadingBestValues}
                        isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission && isOverreadAnalysisSet}
                        notes={sessionNotes}
                        notesErrorStatus={noteError}
                        modalSize={ModalSize.Large}
                        onAddNote={(annotation) => addNewNote(annotation)}
                        onEditNote={(id, comment, annotation) => editNote(id, comment, annotation)}
                    />
                    : null
            }
            <OverreadingAcceptabilityDashboard
                analysisSetId={analysisSetId}
                isViewOnly={isViewOnly || overreadingDisabled}
                fixedCommentsDispatch={fixedCommentDispatchOverreading}
                fixedCommentState={fixedCommentStateOverreading}
                acceptabilityState={acceptabilityState}
                sessionType={SessionTypes.Fvc}
                overreadStatus={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id}
                onNoSessionCommentsSelected={() => {
                    fixedCommentDispatchOverreading(ToggleSessionFixedCommentsVisibilityAction.create());
                }}
                onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
                onCancel={() => GoBackToList(navigate, currentUserPermissions, "/Spirometry", PermissionEnum.OverreadFvcSession, "/Spirometry/Analysis")}
            />
        </React.StrictMode>
    );
}
